// ==========================================
// RNOR Status Calculator
// Accurate FY-wise calculation based on Income Tax Act Section 6
// Supports both USA and UAE NRIs
// ==========================================

/**
 * Indian Financial Year runs from April 1 to March 31
 * FY 2024-25 = April 1, 2024 to March 31, 2025
 */

export interface RNORInput {
  departureDate: Date          // When person left India to become NRI
  returnDate: Date             // When person plans to return to India
  daysInIndiaWhileAbroad: number  // Total days visited India during NRI period
}

export interface FYStatus {
  fy: string                   // e.g., "2025-26"
  fyStartDate: Date
  fyEndDate: Date
  status: 'NRI' | 'RNOR' | 'ROR'
  isResident: boolean
  daysInIndia: number          // Estimated days in India for this FY
  conditionA: {
    met: boolean
    nriYearsIn10: number       // How many of last 10 FYs were NRI
    required: number           // Need 9
  }
  conditionB: {
    met: boolean
    daysIn7Years: number       // Total days in India in last 7 FYs
    threshold: number          // Must be < 730
  }
  explanation: string
}

export interface RNORResult {
  isValid: boolean
  error?: string
  input: RNORInput
  totalYearsAbroad: number
  totalDaysAbroad: number
  fyStatuses: FYStatus[]
  summary: {
    firstRNORYear: string | null
    lastRNORYear: string | null
    rnorDuration: number       // Number of FYs with RNOR status
    firstRORYear: string | null
  }
  methodology: string
}

// ==========================================
// Helper Functions
// ==========================================

/**
 * Get Financial Year string for a given date
 * e.g., Jan 15, 2025 -> "2024-25"
 * e.g., Apr 15, 2025 -> "2025-26"
 */
export function getFYForDate(date: Date): string {
  const year = date.getFullYear()
  const month = date.getMonth() // 0-indexed (0 = Jan, 3 = Apr)

  if (month >= 3) { // April onwards
    return `${year}-${(year + 1).toString().slice(-2)}`
  } else { // Jan-Mar
    return `${year - 1}-${year.toString().slice(-2)}`
  }
}

/**
 * Parse the start year from FY string
 * e.g., "2024-25" -> 2024
 */
function parseFYStartYear(fy: string): number {
  const parts = fy.split('-')
  return parseInt(parts[0] || '0', 10)
}

/**
 * Get start date of a Financial Year
 * e.g., "2024-25" -> April 1, 2024
 */
export function getFYStartDate(fy: string): Date {
  const startYear = parseFYStartYear(fy)
  return new Date(startYear, 3, 1) // April 1
}

/**
 * Get end date of a Financial Year
 * e.g., "2024-25" -> March 31, 2025
 */
export function getFYEndDate(fy: string): Date {
  const startYear = parseFYStartYear(fy)
  return new Date(startYear + 1, 2, 31) // March 31
}

/**
 * Get the previous N Financial Years before a given FY
 * e.g., getPrecedingFYs("2025-26", 10) -> ["2015-16", "2016-17", ..., "2024-25"]
 */
export function getPrecedingFYs(fy: string, count: number): string[] {
  const startYear = parseFYStartYear(fy)
  const result: string[] = []

  for (let i = count; i >= 1; i--) {
    const year = startYear - i
    result.push(`${year}-${(year + 1).toString().slice(-2)}`)
  }

  return result
}

/**
 * Get the next N Financial Years starting from a given FY (inclusive)
 */
export function getNextFYs(fy: string, count: number): string[] {
  const startYear = parseFYStartYear(fy)
  const result: string[] = []

  for (let i = 0; i < count; i++) {
    const year = startYear + i
    result.push(`${year}-${(year + 1).toString().slice(-2)}`)
  }

  return result
}

/**
 * Calculate days between two dates
 */
function daysBetween(start: Date, end: Date): number {
  const msPerDay = 24 * 60 * 60 * 1000
  return Math.floor((end.getTime() - start.getTime()) / msPerDay)
}

/**
 * Calculate days in India for a specific FY given departure and return dates
 */
function calculateDaysInIndiaForFY(
  fy: string,
  departureDate: Date,
  returnDate: Date,
  totalDaysVisitedWhileAbroad: number,
  totalDaysAbroad: number
): number {
  const fyStart = getFYStartDate(fy)
  const fyEnd = getFYEndDate(fy)

  // If FY is entirely before departure, full year in India
  if (fyEnd < departureDate) {
    return 365
  }

  // If FY is entirely after return, full year in India
  if (fyStart > returnDate) {
    return 365
  }

  // If departure is within this FY
  if (departureDate >= fyStart && departureDate <= fyEnd) {
    // Days from FY start to departure
    const daysBeforeDeparture = daysBetween(fyStart, departureDate)

    // If also returning in same FY
    if (returnDate >= fyStart && returnDate <= fyEnd) {
      const daysAfterReturn = daysBetween(returnDate, fyEnd)
      // Pro-rate visits for this FY
      const fyDaysAbroad = daysBetween(departureDate, returnDate)
      const visitDaysThisFY = Math.round((fyDaysAbroad / totalDaysAbroad) * totalDaysVisitedWhileAbroad)
      return daysBeforeDeparture + daysAfterReturn + visitDaysThisFY
    }

    // Departed this FY, returning later
    const fyDaysAbroad = daysBetween(departureDate, fyEnd)
    const visitDaysThisFY = totalDaysAbroad > 0
      ? Math.round((fyDaysAbroad / totalDaysAbroad) * totalDaysVisitedWhileAbroad)
      : 0
    return daysBeforeDeparture + visitDaysThisFY
  }

  // If return is within this FY (and departure was before)
  if (returnDate >= fyStart && returnDate <= fyEnd) {
    const daysAfterReturn = daysBetween(returnDate, fyEnd)
    const fyDaysAbroad = daysBetween(fyStart, returnDate)
    const visitDaysThisFY = totalDaysAbroad > 0
      ? Math.round((fyDaysAbroad / totalDaysAbroad) * totalDaysVisitedWhileAbroad)
      : 0
    return daysAfterReturn + visitDaysThisFY
  }

  // FY is entirely during abroad period
  const visitDaysThisFY = totalDaysAbroad > 0
    ? Math.round((365 / totalDaysAbroad) * totalDaysVisitedWhileAbroad)
    : 0
  return visitDaysThisFY
}

/**
 * Determine if person is Resident for a FY based on days in India
 *
 * Per Section 6(1), a person is Resident if:
 * (a) In India for >= 182 days in the FY, OR
 * (b) In India for >= 60 days in FY AND >= 365 days in preceding 4 FYs
 *
 * Note: For returning NRIs, clause (b) threshold is 182 days (not 60)
 * if they have income from India > 15L and are not liable to tax elsewhere
 *
 * For simplicity, we use the 182-day rule as primary (most common for NRIs)
 */
function isResidentForFY(daysInIndia: number): boolean {
  return daysInIndia >= 182
}

/**
 * Check Condition A: Non-resident in 9 out of 10 preceding FYs
 */
function checkConditionA(
  currentFY: string,
  departureDate: Date,
  returnDate: Date,
  daysVisitedWhileAbroad: number,
  totalDaysAbroad: number
): { met: boolean; nriYearsIn10: number } {
  const preceding10 = getPrecedingFYs(currentFY, 10)

  let nriYears = 0

  for (const fy of preceding10) {
    const daysInIndia = calculateDaysInIndiaForFY(
      fy,
      departureDate,
      returnDate,
      daysVisitedWhileAbroad,
      totalDaysAbroad
    )

    // If not resident (< 182 days), count as NRI year
    if (!isResidentForFY(daysInIndia)) {
      nriYears++
    }
  }

  return {
    met: nriYears >= 9,
    nriYearsIn10: nriYears
  }
}

/**
 * Check Condition B: In India for < 730 days in 7 preceding FYs
 */
function checkConditionB(
  currentFY: string,
  departureDate: Date,
  returnDate: Date,
  daysVisitedWhileAbroad: number,
  totalDaysAbroad: number
): { met: boolean; daysIn7Years: number } {
  const preceding7 = getPrecedingFYs(currentFY, 7)

  let totalDays = 0

  for (const fy of preceding7) {
    const daysInIndia = calculateDaysInIndiaForFY(
      fy,
      departureDate,
      returnDate,
      daysVisitedWhileAbroad,
      totalDaysAbroad
    )
    totalDays += daysInIndia
  }

  return {
    met: totalDays < 730,
    daysIn7Years: totalDays
  }
}

// ==========================================
// Main Calculator Function
// ==========================================

export function calculateRNORStatus(input: RNORInput): RNORResult {
  const { departureDate, returnDate, daysInIndiaWhileAbroad } = input

  // Validation
  if (returnDate <= departureDate) {
    return {
      isValid: false,
      error: 'Return date must be after departure date',
      input,
      totalYearsAbroad: 0,
      totalDaysAbroad: 0,
      fyStatuses: [],
      summary: {
        firstRNORYear: null,
        lastRNORYear: null,
        rnorDuration: 0,
        firstRORYear: null
      },
      methodology: ''
    }
  }

  const totalDaysAbroad = daysBetween(departureDate, returnDate)
  const totalYearsAbroad = totalDaysAbroad / 365

  if (daysInIndiaWhileAbroad > totalDaysAbroad) {
    return {
      isValid: false,
      error: 'Days in India while abroad cannot exceed total days abroad',
      input,
      totalYearsAbroad,
      totalDaysAbroad,
      fyStatuses: [],
      summary: {
        firstRNORYear: null,
        lastRNORYear: null,
        rnorDuration: 0,
        firstRORYear: null
      },
      methodology: ''
    }
  }

  // Determine which FYs to calculate
  // Start from the FY of return and go 5 years forward
  const returnFY = getFYForDate(returnDate)
  const fysToCalculate = getNextFYs(returnFY, 6)

  const fyStatuses: FYStatus[] = []

  for (const fy of fysToCalculate) {
    const fyStart = getFYStartDate(fy)
    const fyEnd = getFYEndDate(fy)

    // Calculate days in India for this FY
    const daysInIndia = calculateDaysInIndiaForFY(
      fy,
      departureDate,
      returnDate,
      daysInIndiaWhileAbroad,
      totalDaysAbroad
    )

    // Check if resident
    const isResident = isResidentForFY(daysInIndia)

    // If not resident, status is NRI
    if (!isResident) {
      fyStatuses.push({
        fy,
        fyStartDate: fyStart,
        fyEndDate: fyEnd,
        status: 'NRI',
        isResident: false,
        daysInIndia,
        conditionA: { met: false, nriYearsIn10: 0, required: 9 },
        conditionB: { met: false, daysIn7Years: 0, threshold: 730 },
        explanation: `Less than 182 days in India (${daysInIndia} days). Status: Non-Resident.`
      })
      continue
    }

    // Person is Resident - check RNOR conditions
    const condA = checkConditionA(fy, departureDate, returnDate, daysInIndiaWhileAbroad, totalDaysAbroad)
    const condB = checkConditionB(fy, departureDate, returnDate, daysInIndiaWhileAbroad, totalDaysAbroad)

    // RNOR if EITHER condition is met
    const isRNOR = condA.met || condB.met

    let explanation = ''
    if (isRNOR) {
      const reasons: string[] = []
      if (condA.met) {
        reasons.push(`Non-resident in ${condA.nriYearsIn10}/10 preceding years (need ≥9)`)
      }
      if (condB.met) {
        reasons.push(`Only ${condB.daysIn7Years} days in India in 7 preceding years (need <730)`)
      }
      explanation = `RNOR: ${reasons.join(' AND ')}`
    } else {
      explanation = `ROR: Non-resident in only ${condA.nriYearsIn10}/10 years (need ≥9) AND ${condB.daysIn7Years} days in 7 years (need <730)`
    }

    fyStatuses.push({
      fy,
      fyStartDate: fyStart,
      fyEndDate: fyEnd,
      status: isRNOR ? 'RNOR' : 'ROR',
      isResident: true,
      daysInIndia,
      conditionA: {
        met: condA.met,
        nriYearsIn10: condA.nriYearsIn10,
        required: 9
      },
      conditionB: {
        met: condB.met,
        daysIn7Years: condB.daysIn7Years,
        threshold: 730
      },
      explanation
    })
  }

  // Calculate summary
  const rnorYears = fyStatuses.filter(s => s.status === 'RNOR')
  const rorYears = fyStatuses.filter(s => s.status === 'ROR')

  const firstRNOR = rnorYears[0]
  const lastRNOR = rnorYears[rnorYears.length - 1]
  const firstROR = rorYears[0]

  const summary = {
    firstRNORYear: firstRNOR ? firstRNOR.fy : null,
    lastRNORYear: lastRNOR ? lastRNOR.fy : null,
    rnorDuration: rnorYears.length,
    firstRORYear: firstROR ? firstROR.fy : null
  }

  const methodology = `
RNOR Calculation Methodology (Income Tax Act Section 6):

1. RESIDENCY TEST: A person is "Resident" if present in India for ≥182 days in a Financial Year.

2. RNOR CONDITIONS: A Resident qualifies as RNOR (not ROR) if EITHER:
   • Condition A: Non-resident in India in ≥9 of the 10 preceding Financial Years, OR
   • Condition B: Present in India for ≤729 days during the 7 preceding Financial Years

3. YOUR CALCULATION:
   • Departed India: ${departureDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
   • Returning to India: ${returnDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
   • Total time abroad: ${totalYearsAbroad.toFixed(1)} years (${totalDaysAbroad} days)
   • Days in India while abroad: ${daysInIndiaWhileAbroad} days

4. IMPORTANT NOTES:
   • Financial Year (FY) runs April 1 to March 31
   • RNOR status means foreign income is NOT taxable in India
   • After RNOR period, global income becomes taxable (ROR status)
`.trim()

  return {
    isValid: true,
    input,
    totalYearsAbroad,
    totalDaysAbroad,
    fyStatuses,
    summary,
    methodology
  }
}

/**
 * Quick RNOR estimate based on years abroad and estimated visits
 * Used when detailed dates are not available (e.g., wizard Step 1)
 *
 * RNOR requires EITHER:
 * - Condition A: NRI for 9+ of last 10 FYs, OR
 * - Condition B: ≤729 days in India in last 7 FYs
 */
export interface QuickRNORResult {
  eligible: boolean
  estimatedYears: number
  conditionA: {
    met: boolean
    yearsAsNRI: number
    required: number
  }
  conditionB: {
    met: boolean
    estimatedDays: number
    threshold: number
  }
  explanation: string
  recommendation: string
  /** Warning about residency prerequisite */
  residencyWarning: string
  /** Display text for years (e.g., "up to 3 years") */
  yearsDisplay: string
}

export function quickRNOREstimate(
  nriSinceYear: number,
  returnYear: number,
  estimatedDaysPerYear: number = 45 // Default: ~45 days/year visits to India
): QuickRNORResult {
  const yearsAbroad = returnYear - nriSinceYear

  // Condition A: NRI for 9+ of last 10 years
  const conditionAMet = yearsAbroad >= 9

  // Condition B: ≤729 days in India in last 7 PRECEDING FYs
  // CRITICAL: This must count ALL days in India, including:
  // 1. Days when they LIVED in India (before becoming NRI)
  // 2. Days VISITING India (while NRI)
  //
  // Example: If NRI since 2024, returning 2027 (3 years abroad):
  // - 7-year window: 2020-2027
  // - Years lived in India within window: 7 - 3 = 4 years = ~1460 days
  // - Years visiting while NRI: 3 years * 45 days = 135 days
  // - Total: 1460 + 135 = 1595 days >> 729 (NOT eligible)

  const yearsInIndiaWithin7YrWindow = Math.max(0, 7 - yearsAbroad)
  const daysLivedInIndia = yearsInIndiaWithin7YrWindow * 365
  const yearsAbroadWithin7YrWindow = Math.min(yearsAbroad, 7)
  const daysVisitingIndia = yearsAbroadWithin7YrWindow * estimatedDaysPerYear
  const estimatedDaysIn7Years = daysLivedInIndia + daysVisitingIndia
  const conditionBMet = estimatedDaysIn7Years <= 729

  // RNOR if EITHER condition is met
  const eligible = conditionAMet || conditionBMet

  // Estimate RNOR duration based on both conditions
  let estimatedYears = 0
  if (eligible) {
    // Duration depends on how strongly conditions are met
    if (yearsAbroad >= 15 && estimatedDaysIn7Years < 400) {
      estimatedYears = 3
    } else if (yearsAbroad >= 9 || estimatedDaysIn7Years < 500) {
      estimatedYears = 2
    } else {
      estimatedYears = 1
    }
  }

  // Build explanation
  let explanation = ''
  let recommendation = ''

  if (eligible) {
    const reasons: string[] = []
    if (conditionAMet) {
      reasons.push(`✓ ${yearsAbroad} years as NRI (≥9 required)`)
    }
    if (conditionBMet) {
      reasons.push(`✓ ~${estimatedDaysIn7Years} days in India over 7 years (≤729 required)`)
    }
    explanation = `You qualify for RNOR! ${reasons.join('; ')}`
    recommendation = `Estimated ${estimatedYears} years of RNOR status where foreign income is tax-free.`
  } else {
    explanation = `${yearsAbroad} years as NRI (need ≥9 for Condition A). Estimated ~${estimatedDaysIn7Years} days in India over 7 years (need ≤729 for Condition B).`

    const yearsNeeded = 9 - yearsAbroad

    // Provide actionable recommendation based on how close they are to qualifying
    if (yearsNeeded <= 3) {
      recommendation = `Stay abroad ${yearsNeeded} more year${yearsNeeded > 1 ? 's' : ''} to qualify via Condition A. Consider delaying your return.`
    } else if (yearsNeeded <= 6) {
      recommendation = `You need ${yearsNeeded} more years abroad to qualify. Plan your return timeline carefully to maximize tax benefits.`
    } else {
      // They're far from qualifying - give realistic advice
      recommendation = `RNOR requires 9+ years abroad or minimal India presence. Plan to liquidate taxable foreign assets before returning.`
    }
  }

  // Residency warning - RNOR requires being Resident first (182+ days in return FY)
  const residencyWarning = eligible
    ? 'Important: RNOR status requires you to be a Resident first (182+ days in India in the return FY). If returning late in the FY (e.g., after October), your RNOR window may start in the following FY.'
    : ''

  // Display text uses "up to" since actual years depend on year-by-year recalculation
  const yearsDisplay = eligible
    ? `up to ${estimatedYears} year${estimatedYears > 1 ? 's' : ''}`
    : '0 years'

  return {
    eligible,
    estimatedYears,
    conditionA: {
      met: conditionAMet,
      yearsAsNRI: yearsAbroad,
      required: 9
    },
    conditionB: {
      met: conditionBMet,
      estimatedDays: estimatedDaysIn7Years,
      threshold: 729
    },
    explanation,
    recommendation,
    residencyWarning,
    yearsDisplay
  }
}

export default calculateRNORStatus
